import React, {useEffect, useState} from "react";
import MonacoEditor, {useMonaco} from "@monaco-editor/react";
import {KSpinner, noop, tooltipate} from "@kopjra/uikit";
import {TObject} from "@sinclair/typebox";
import {Form} from "react-bootstrap";

export interface Props {
    id: string;
    required?: boolean;
    readOnly?: boolean;
    onValidate?: (valid: boolean) => void;
    onChange?: (json?: object) => void;
    schema?: TObject;
    label?: string | JSX.Element;
    tooltip?: string |JSX.Element;
    errorMessage?: string | JSX.Element | Map<"required" | "json", string | JSX.Element>;
    defaultValue?: object;
}

export const JsonEditor: React.FC<Props> = ({defaultValue, errorMessage, tooltip, label, required, id, schema, readOnly = false, onChange, onValidate}) => {
    const monaco = useMonaco();
    const [initialized, setInitialized] = useState(false);
    const [value, setValue] = useState<string | undefined>();
    const [errorMsg, setErrorMsg] = useState<string | undefined | JSX.Element>(required ? (errorMessage instanceof Map ? errorMessage.get("required") : errorMessage) : undefined);

    useEffect(() => {
        if (monaco) {
            const themeData = {
                "base": "vs",
                "inherit": true,
                "rules": [
                    {
                        "background": "FFFFFF",
                        "token": ""
                    },
                    {
                        "foreground": "008e00",
                        "token": "comment"
                    },
                    {
                        "foreground": "7d4726",
                        "token": "meta.preprocessor"
                    },
                    {
                        "foreground": "7d4726",
                        "token": "keyword.control.import"
                    },
                    {
                        "foreground": "df0002",
                        "token": "string"
                    },
                    {
                        "foreground": "3a00dc",
                        "token": "constant.numeric"
                    },
                    {
                        "foreground": "c800a4",
                        "token": "constant.language"
                    },
                    {
                        "foreground": "275a5e",
                        "token": "constant.character"
                    },
                    {
                        "foreground": "275a5e",
                        "token": "constant.other"
                    },
                    {
                        "foreground": "c800a4",
                        "token": "variable.language"
                    },
                    {
                        "foreground": "c800a4",
                        "token": "variable.other"
                    },
                    {
                        "foreground": "c800a4",
                        "token": "keyword"
                    },
                    {
                        "foreground": "c900a4",
                        "token": "storage"
                    },
                    {
                        "foreground": "438288",
                        "token": "entity.name.class"
                    },
                    {
                        "foreground": "790ead",
                        "token": "entity.name.tag"
                    },
                    {
                        "foreground": "450084",
                        "token": "entity.other.attribute-name"
                    },
                    {
                        "foreground": "450084",
                        "token": "support.function"
                    },
                    {
                        "foreground": "450084",
                        "token": "support.constant"
                    },
                    {
                        "foreground": "790ead",
                        "token": "support.type"
                    },
                    {
                        "foreground": "790ead",
                        "token": "support.class"
                    },
                    {
                        "foreground": "790ead",
                        "token": "support.other.variable"
                    }
                ],
                "colors": {
                    "editor.foreground": "#000000",
                    "editor.background": "#FFFFFF",
                    "editor.selectionBackground": "#B5D5FF",
                    "editor.lineHighlightBackground": "#00000012",
                    "editorCursor.foreground": "#000000",
                    "editorWhitespace.foreground": "#BFBFBF"
                }
            };
            monaco.editor.defineTheme("myTheme", themeData as any);
            const options: any = {
                validate: true,
            };
            if (schema) {
                options.schemas = [
                    {
                        uri: "http://signatureCustomisatio.ns",
                        fileMatch: ["*"],
                        schema,
                    }
                ];
            }
            monaco.languages.json.jsonDefaults.setDiagnosticsOptions(options);
            setInitialized(true);
        }
    }, [monaco, setInitialized, schema]);

    return (
        <Form.Group controlId={id} className="k-input-group k-monaco-editor">
            <Form.Control
                name={id}
                type={"text"}
                required={required}
                className={`inner-input`}
                onChange={() => noop()}
                value={value}
            />
            {initialized ? (
                <MonacoEditor
                    language={"json"}
                    width={"100%"}
                    height={"400px"}
                    theme={"myTheme"}
                    options={{
                        readOnly,
                        minimap: {
                            enabled: false
                        }
                    }}
                    defaultValue={defaultValue ? JSON.stringify(defaultValue, null, "    ") : undefined}
                    onValidate={(markers) => {
                        if (onValidate) {
                            onValidate(!markers.length);
                        }
                        const input = $(`#${id}`);
                        (input.get(0) as HTMLInputElement).setCustomValidity("");

                        if (required && !value) {
                            (input.get(0) as HTMLInputElement).setCustomValidity("error");
                            if (errorMessage instanceof Map) {
                                setErrorMsg(errorMessage.get("required"));
                            } else {
                                setErrorMsg(errorMessage);
                            }
                        } else if (markers.length) {
                            (input.get(0) as HTMLInputElement).setCustomValidity("error");
                            if (errorMessage instanceof Map) {
                                setErrorMsg(errorMessage.get("json"));
                            } else {
                                setErrorMsg(errorMessage);
                            }
                        }
                    }}
                    onChange={(value) => {
                        if (onChange) {
                            let json: object | undefined;
                            if (value) {
                                try {
                                    json = JSON.parse(value);
                                } catch {
                                    json = undefined;
                                }
                            }
                            onChange(json);
                        }
                        setValue(value);
                    }}
                />
            ) : (
                <KSpinner/>
            )}
            {label ? (
                <div className="label-holder">
                    {tooltipate(<Form.Label className={`k-input-label${required ? " required" : ""}`}>{label}</Form.Label>, tooltip, undefined, true)}
                    {errorMsg ? (
                        <Form.Control.Feedback as={"span"} type="invalid">
                            {errorMsg}
                        </Form.Control.Feedback>
                    ) : null}
                </div>
            ) : errorMsg ? (
                <Form.Control.Feedback as={"span"} type="invalid">
                    {errorMsg}
                </Form.Control.Feedback>
            ) : null}
        </Form.Group>
    );
}
